import React, { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { PageHeader, Container } from "@/components/PageShell";
import { Plus, QrCode, Edit2, Trash2, Copy, X, Loader2, ToggleLeft, ToggleRight, Download, Package } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full px-3 py-2 bg-white border border-zinc-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#FA3E05] focus:border-[#FA3E05]";
const btnPrimary = "bg-[#FA3E05] hover:bg-[#E03504] disabled:opacity-60 text-white text-sm font-medium rounded-md px-4 py-2 transition-colors inline-flex items-center gap-2";
const btnSecondary = "bg-white border border-zinc-200 hover:bg-zinc-50 text-zinc-900 text-sm font-medium rounded-md px-4 py-2 transition-colors inline-flex items-center gap-2";
const btnGhost = "hover:bg-zinc-100 text-zinc-600 hover:text-zinc-900 text-sm rounded-md px-2.5 py-1.5 transition-colors inline-flex items-center gap-1.5";

function Field({ label, children }) {
    return (
        <div className="space-y-1">
            <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">{label}</label>
            {children}
        </div>
    );
}

function UserForm({ initial, servers, chains, onClose, onSaved }) {
    const [data, setData] = useState(initial || { email: "", server_id: "", chain_id: "", enabled: true, fingerprint_override: "" });
    const [mode, setMode] = useState(initial?.chain_id ? "chain" : "server");
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true); setErr("");
        try {
            const payload = {
                email: data.email,
                enabled: data.enabled,
                server_id: mode === "server" ? (data.server_id || null) : null,
                chain_id: mode === "chain" ? (data.chain_id || null) : null,
                fingerprint_override: data.fingerprint_override || null,
            };
            if (initial) await api.put(`/vpn-users/${initial.id}`, payload);
            else await api.post("/vpn-users", payload);
            toast.success(initial ? "Пользователь обновлён" : "Пользователь создан");
            onSaved();
        } catch (e) { setErr(extractError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 bg-zinc-950/30 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white border border-zinc-200 rounded-md shadow-xl w-full max-w-lg" data-testid="user-form-modal">
                <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                    <h2 className="text-base font-semibold">{initial ? "Редактировать пользователя" : "Новый пользователь VPN"}</h2>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <Field label="Метка / Email">
                        <input required data-testid="user-email-input" className={inputCls} value={data.email} onChange={(e) => setData({ ...data, email: e.target.value })} placeholder="user@example.com или home-laptop" />
                    </Field>

                    <div>
                        <label className="text-[11px] font-medium text-zinc-600 uppercase tracking-wider">Тип подключения</label>
                        <div className="flex gap-2 mt-1">
                            <button type="button" data-testid="mode-server-btn" onClick={() => setMode("server")} className={`flex-1 text-sm py-2 px-3 rounded-md border transition-colors ${mode === "server" ? "bg-[#FA3E05] text-white border-[#FA3E05]" : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}>
                                Прямой VPN (один сервер)
                            </button>
                            <button type="button" data-testid="mode-chain-btn" onClick={() => setMode("chain")} className={`flex-1 text-sm py-2 px-3 rounded-md border transition-colors ${mode === "chain" ? "bg-[#FA3E05] text-white border-[#FA3E05]" : "bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50"}`}>
                                Маршрутная цепочка
                            </button>
                        </div>
                        <p className="text-[11px] text-zinc-500 mt-1.5">
                            {mode === "server"
                                ? "Прямой VPN: трафик уходит в интернет с этого сервера. Подходит когда нужен российский IP в обход провайдерской блокировки (Telegram и т.п.)."
                                : "Цепочка: трафик идёт через RU Relay → Foreign Exit. Подходит для обхода блокировок РФ — Spotify, ChatGPT, заблокированные сайты."}
                        </p>
                    </div>

                    {mode === "server" ? (
                        <Field label="Сервер">
                            <select data-testid="user-server-select" className={inputCls} value={data.server_id || ""} onChange={(e) => setData({ ...data, server_id: e.target.value })} required>
                                <option value="">— выбрать —</option>
                                {servers.filter((s) => s.role !== "foreign_exit" || true).map((s) => (
                                    <option key={s.id} value={s.id}>{s.name} · {s.host} · {s.role}</option>
                                ))}
                            </select>
                        </Field>
                    ) : (
                        <Field label="Цепочка">
                            <select data-testid="user-chain-select" className={inputCls} value={data.chain_id || ""} onChange={(e) => setData({ ...data, chain_id: e.target.value })} required>
                                <option value="">— выбрать —</option>
                                {chains.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                            {chains.length === 0 && <div className="text-xs text-zinc-500 mt-1">Создайте цепочку на странице &quot;Маршруты&quot;</div>}
                        </Field>
                    )}

                    <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                        <input type="checkbox" checked={data.enabled} onChange={(e) => setData({ ...data, enabled: e.target.checked })} data-testid="user-enabled-checkbox" />
                        Активен
                    </label>

                    <Field label="Browser fingerprint (опционально)">
                        <select
                            data-testid="user-fingerprint-override"
                            className={inputCls}
                            value={data.fingerprint_override || ""}
                            onChange={(e) => setData({ ...data, fingerprint_override: e.target.value })}
                        >
                            <option value="">— Дефолт профиля</option>
                            <option value="chrome">chrome</option>
                            <option value="firefox">firefox</option>
                            <option value="safari">safari</option>
                            <option value="ios">ios (мобильный Safari)</option>
                            <option value="android">android (Chrome Android)</option>
                            <option value="edge">edge</option>
                            <option value="random">random (рандом каждый коннект)</option>
                        </select>
                        <p className="text-[11px] text-zinc-500 mt-1">
                            Этот юзер будет имитировать TLS handshake выбранного браузера. Полезно если несколько устройств с одной меткой и хочется разные fingerprint&apos;ы.
                        </p>
                    </Field>

                    {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">{err}</div>}
                </div>
                <div className="px-5 py-3 border-t border-zinc-200 flex justify-end gap-2 bg-zinc-50">
                    <button type="button" onClick={onClose} className={btnSecondary}>Отмена</button>
                    <button type="submit" disabled={saving} className={btnPrimary} data-testid="user-form-submit">
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {initial ? "Сохранить" : "Создать"}
                    </button>
                </div>
            </form>
        </div>
    );
}

function QrModal({ user, onClose }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [activeIdx, setActiveIdx] = useState(0);
    const [bypassRu, setBypassRu] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [bundleDownloading, setBundleDownloading] = useState(false);

    useEffect(() => {
        api.get(`/vpn-users/${user.id}/configs`)
            .then((r) => setData(r.data))
            .catch((e) => setErr(extractError(e)));
    }, [user.id]);

    const active = data?.configs?.[activeIdx];

    const copy = () => {
        if (!active) return;
        navigator.clipboard.writeText(active.url);
        toast.success("Ссылка скопирована");
    };

    const downloadOne = async () => {
        if (!active) return;
        setDownloading(true);
        try {
            const res = await api.get(`/vpn-users/${user.id}/client-config`, {
                params: { bypass_ru: bypassRu, download: true, profile_id: active.profile_id },
                responseType: "blob",
            });
            const cd = res.headers["content-disposition"] || "";
            const match = cd.match(/filename="?([^";]+)"?/);
            const filename = match ? match[1] : `vpn-${user.email}-${active.profile_name}.json`;
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Скачан: ${filename}`);
        } catch (e) { toast.error(extractError(e)); }
        finally { setDownloading(false); }
    };

    const downloadBundle = async () => {
        setBundleDownloading(true);
        try {
            const res = await api.get(`/vpn-users/${user.id}/client-config-bundle`, {
                params: { bypass_ru: bypassRu },
                responseType: "blob",
            });
            const filename = `vpn-${user.email.replace("@", "_")}-configs.zip`;
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("ZIP со всеми конфигами скачан");
        } catch (e) { toast.error(extractError(e)); }
        finally { setBundleDownloading(false); }
    };

    const protoBadge = (proto) => {
        const map = {
            "vless-reality": { t: "Reality", cls: "bg-zinc-100 text-zinc-800" },
            "vless-tls": { t: "VLESS+TLS", cls: "bg-blue-50 text-blue-700" },
            "trojan": { t: "Trojan", cls: "bg-orange-50 text-orange-700" },
        };
        const c = map[proto] || { t: proto, cls: "bg-zinc-100 text-zinc-800" };
        return <span className={`inline-block text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${c.cls}`}>{c.t}</span>;
    };

    return (
        <div className="fixed inset-0 bg-zinc-950/40 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in" onClick={onClose}>
            <div className="bg-white border border-zinc-200 rounded-md shadow-xl w-full max-w-2xl max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="qr-modal">
                <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
                    <div>
                        <h2 className="text-base font-semibold">Конфиги пользователя</h2>
                        <div className="text-xs text-zinc-500 mt-0.5">{user.email} · <span className="font-mono">{user.uuid.slice(0, 8)}…</span></div>
                    </div>
                    <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X className="w-4 h-4" /></button>
                </div>

                <div className="p-5">
                    {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">{err}</div>}
                    {!data && !err && <div className="text-sm text-zinc-500">Загрузка…</div>}
                    {data && data.configs.length === 0 && (
                        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md px-3 py-2.5">
                            На этом сервере нет активных профилей с настроенным доменом. Зайдите на страницу серверов → раскройте сервер → активируйте профили или укажите домен.
                        </div>
                    )}
                    {data && data.configs.length > 0 && (
                        <>
                            {/* Profile tabs */}
                            <div className="flex gap-1 bg-zinc-100 rounded-md p-1 mb-4">
                                {data.configs.map((c, i) => (
                                    <button
                                        key={c.profile_id}
                                        onClick={() => setActiveIdx(i)}
                                        data-testid={`profile-tab-${i}`}
                                        className={`flex-1 py-2 px-3 text-xs font-medium rounded transition-colors text-left ${activeIdx === i ? "bg-white shadow-sm text-zinc-900" : "text-zinc-600 hover:text-zinc-900"}`}
                                    >
                                        <div className="font-semibold truncate">{c.profile_name}</div>
                                        <div className="mt-0.5 flex items-center gap-1.5">
                                            {protoBadge(c.protocol)}
                                            <span className="font-mono text-zinc-500">{c.transport} :{c.port}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>

                            {/* Active profile QR + URL */}
                            <div className="grid grid-cols-[auto_1fr] gap-4">
                                <div className="bg-zinc-50 border border-zinc-200 rounded-md p-3 flex items-center justify-center">
                                    <img src={active.qr} alt="QR" className="w-40 h-40" data-testid="qr-image" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-medium mb-1">URL импорта (мобильный)</div>
                                    <div className="relative">
                                        <div className="font-mono text-[11px] bg-zinc-50 border border-zinc-200 rounded-md p-2.5 pr-9 break-all max-h-28 overflow-auto" data-testid="vless-url">
                                            {active.url}
                                        </div>
                                        <button onClick={copy} className="absolute top-1.5 right-1.5 p-1.5 bg-white border border-zinc-200 rounded hover:bg-zinc-50" data-testid="copy-url-btn" title="Копировать">
                                            <Copy className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                    <div className="mt-2 text-[11px] text-zinc-500">
                                        QR работает в v2rayNG (Android) и Shadowrocket (iOS).
                                    </div>
                                </div>
                            </div>

                            {/* Download section */}
                            <div className="mt-5 bg-orange-50/50 border border-orange-200 rounded-md p-4 space-y-3">
                                <div>
                                    <div className="text-sm font-semibold text-zinc-900">Полный клиентский конфиг (v2rayN)</div>
                                    <div className="text-xs text-zinc-600 mt-1">
                                        JSON-файл с уже настроенным split routing. Импортируется в v2rayN → «Серверы → Import custom config from file».
                                    </div>
                                </div>

                                <label className="flex items-center gap-2 cursor-pointer select-none">
                                    <input type="checkbox" checked={bypassRu} onChange={(e) => setBypassRu(e.target.checked)} data-testid="bypass-ru-checkbox" className="accent-[#FA3E05]" />
                                    <span className="text-sm text-zinc-800">
                                        Включить обход РФ (<span className="font-mono">.ru/.рф</span>, Госуслуги, Сбер, VK, Яндекс — напрямую)
                                    </span>
                                </label>

                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        onClick={downloadOne}
                                        disabled={downloading}
                                        data-testid="download-one-btn"
                                        className="bg-white border border-zinc-200 hover:bg-zinc-50 disabled:opacity-60 text-zinc-900 text-sm font-medium rounded-md py-2.5 transition-colors inline-flex items-center justify-center gap-2"
                                    >
                                        {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                                        Только «{active.profile_name}»
                                    </button>
                                    <button
                                        onClick={downloadBundle}
                                        disabled={bundleDownloading}
                                        data-testid="download-bundle-btn"
                                        className="bg-[#FA3E05] hover:bg-[#E03504] disabled:opacity-60 text-white text-sm font-medium rounded-md py-2.5 transition-colors inline-flex items-center justify-center gap-2"
                                    >
                                        {bundleDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
                                        ZIP со всеми ({data.configs.length})
                                    </button>
                                </div>
                            </div>

                            <div className="mt-3 text-[11px] text-zinc-500">
                                <b>Как раздавать:</b> скачайте ZIP → отправьте пользователю одним файлом. Внутри 3 JSON-файла (01, 02, 03) + README с инструкцией — пользователь попробует первый; если заблокирован — переключится на следующий в v2rayN.
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function Users() {
    const [users, setUsers] = useState([]);
    const [servers, setServers] = useState([]);
    const [chains, setChains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [newOpen, setNewOpen] = useState(false);
    const [qrUser, setQrUser] = useState(null);

    const load = async () => {
        try {
            const [u, s, c] = await Promise.all([
                api.get("/vpn-users"),
                api.get("/servers"),
                api.get("/chains"),
            ]);
            setUsers(u.data.filter((x) => !x.email.startsWith("chain-bridge-")));
            setServers(s.data);
            setChains(c.data);
        } catch (e) { toast.error(extractError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const toggle = async (u) => {
        try {
            await api.put(`/vpn-users/${u.id}`, {
                email: u.email, enabled: !u.enabled,
                server_id: u.server_id, chain_id: u.chain_id,
            });
            load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const del = async (id) => {
        if (!window.confirm("Удалить пользователя?")) return;
        try {
            await api.delete(`/vpn-users/${id}`);
            toast.success("Пользователь удалён");
            load();
        } catch (e) { toast.error(extractError(e)); }
    };

    const serverNameOf = (id) => servers.find((s) => s.id === id)?.name || "—";
    const chainNameOf = (id) => chains.find((c) => c.id === id)?.name || "—";

    return (
        <>
            <PageHeader
                title="Пользователи"
                subtitle="Клиенты с защищённым подключением"
                testid="users-header"
                actions={
                    <button onClick={() => setNewOpen(true)} className={btnPrimary} data-testid="add-user-btn">
                        <Plus className="w-4 h-4" /> Добавить пользователя
                    </button>
                }
            />
            <Container>
                <div className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid="users-table">
                    {loading ? (
                        <div className="p-10 text-center text-sm text-zinc-500">Загрузка…</div>
                    ) : users.length === 0 ? (
                        <div className="p-14 text-center">
                            <div className="text-sm text-zinc-700 font-medium">Пользователей пока нет</div>
                            <div className="text-xs text-zinc-500 mt-1">Создайте пользователя — система сгенерирует UUID и ссылку подключения с QR-кодом</div>
                        </div>
                    ) : (
                        <table className="w-full text-sm whitespace-nowrap">
                            <thead>
                                <tr className="bg-zinc-50 border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-zinc-500">
                                    <th className="px-4 py-3 font-medium">Email / Метка</th>
                                    <th className="px-4 py-3 font-medium">UUID</th>
                                    <th className="px-4 py-3 font-medium">Маршрут</th>
                                    <th className="px-4 py-3 font-medium">Активен</th>
                                    <th className="px-4 py-3 font-medium text-right">Действия</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50/50" data-testid={`user-row-${u.id}`}>
                                        <td className="px-4 py-3 font-medium">{u.email}</td>
                                        <td className="px-4 py-3 font-mono text-xs text-zinc-600">{u.uuid.slice(0, 8)}…{u.uuid.slice(-4)}</td>
                                        <td className="px-4 py-3 text-xs text-zinc-700">
                                            {u.server_id ? <>Сервер: <span className="font-medium">{serverNameOf(u.server_id)}</span></> :
                                                u.chain_id ? <>Цепочка: <span className="font-medium">{chainNameOf(u.chain_id)}</span></> :
                                                    <span className="text-zinc-400">—</span>}
                                        </td>
                                        <td className="px-4 py-3">
                                            <button onClick={() => toggle(u)} data-testid={`toggle-${u.id}`} className="text-zinc-600 hover:text-[#FA3E05]">
                                                {u.enabled ? <ToggleRight className="w-5 h-5 text-emerald-500" /> : <ToggleLeft className="w-5 h-5 text-zinc-400" />}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => setQrUser(u)} disabled={!u.server_id && !u.chain_id} className={`${btnGhost} disabled:opacity-40 disabled:cursor-not-allowed`} data-testid={`qr-${u.id}`}>
                                                    <QrCode className="w-3.5 h-3.5" /> QR
                                                </button>
                                                <button onClick={() => setEditing(u)} className={btnGhost} data-testid={`edit-user-${u.id}`}>
                                                    <Edit2 className="w-3.5 h-3.5" />
                                                </button>
                                                <button onClick={() => del(u.id)} className={`${btnGhost} hover:!text-red-600`} data-testid={`delete-user-${u.id}`}>
                                                    <Trash2 className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </Container>

            {(newOpen || editing) && (
                <UserForm
                    initial={editing}
                    servers={servers}
                    chains={chains}
                    onClose={() => { setNewOpen(false); setEditing(null); }}
                    onSaved={() => { setNewOpen(false); setEditing(null); load(); }}
                />
            )}
            {qrUser && <QrModal user={qrUser} onClose={() => setQrUser(null)} />}
        </>
    );
}
